import {FC} from 'react'
import type {ViewerErrorKind} from './handler-structures'

/**
 * Content shown inside a viewer tab that was opened optimistically on double-click / a
 * viewer-opening context-menu action (B1/B2). While the OBserver resolves the viewer it shows a
 * spinner; if opening fails it shows the error in place — the tab is never silently closed, so the
 * user always sees the outcome of their click. Replaced by the real viewer once metadata arrives.
 *
 * The error state is a neutral notice card rather than a red alert: most often it just means no
 * viewer exists for the file's type yet, which is expected, not alarming.
 */
interface ViewerLoadingPlaceholderProps {
  /** Loading message; ignored when {@link error} is set. */
  message?: string
  /** File/object name, shown as a secondary line for context. */
  name?: string
  /** When set, render an error state (opening failed) instead of the spinner. */
  error?: string
  /** Picks the error heading: an unsupported file type, or any other failure. */
  errorKind?: ViewerErrorKind
}

const ERROR_HEADINGS: Record<ViewerErrorKind, string> = {
  unsupported: 'This file type is not (yet) supported.',
  failed: 'This file could not be opened.',
}

const ViewerLoadingPlaceholder: FC<ViewerLoadingPlaceholderProps> = ({
  message = 'Loading file viewer…',
  name,
  error,
  errorKind = 'failed',
}) => {
  const fileName = name && (
    <div style={{fontSize: '0.85rem', opacity: 0.75, wordBreak: 'break-all'}}>{name}</div>
  )

  return (
    <div
      role={error ? 'alert' : 'status'}
      aria-live='polite'
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--ob-spacing-4, 16px)',
        height: '100%',
        width: '100%',
        color: 'var(--ob-gray-600, #6c757d)',
        textAlign: 'center',
        padding: 'var(--ob-spacing-6, 24px)',
      }}
    >
      {error ? (
        <div
          className='card shadow-sm'
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--ob-spacing-3, 12px)',
            maxWidth: '32rem',
            padding: 'var(--ob-spacing-6, 24px) var(--ob-spacing-8, 32px)',
            color: 'var(--ob-text-secondary, #524e43)',
            background: 'var(--ob-card-bg, var(--bs-card-bg))',
            borderColor: 'var(--ob-border-color, var(--bs-border-color))',
          }}
        >
          <i
            className={errorKind === 'unsupported' ? 'fas fa-file-circle-question' : 'fas fa-circle-info'}
            aria-hidden='true'
            style={{fontSize: '2rem', color: 'var(--ob-text-muted, #70695c)'}}
          />
          <div style={{fontSize: '1.05rem', fontWeight: 500, color: 'var(--ob-text-primary, #3b3729)'}}>
            {ERROR_HEADINGS[errorKind]}
          </div>
          <div style={{fontSize: '0.9rem'}}>{error}</div>
          {fileName}
        </div>
      ) : (
        <>
          <span
            className='spinner-border text-primary'
            role='presentation'
            aria-hidden='true'
            style={{width: '2.5rem', height: '2.5rem'}}
          />
          <div style={{fontSize: '1.05rem', fontWeight: 500}}>{message}</div>
          {fileName}
        </>
      )}
    </div>
  )
}

export default ViewerLoadingPlaceholder
